import {
  ApiResponse,
  AuthRequest,
  SignupRequest,
  AuthResponse,
  MovieSwapResult,
  FilmDetail,
  RatingDeviationResponse,
} from "../../shared/types";
import { ApiError } from "../lib/apiError";
import {
  LBFilm,
  MFLScoringMetric,
  MFLMovieScore,
  MFLCatalogueFilm,
  MFLPick,
  MFLRoster,
  MFLRosterView,
  MFLLeaderboards,
  AwardShow,
  EventSummary,
  EventData,
  EventUserPick,
  RefreshJob,
  UserScrapeJob,
  AccountView,
  AccountUpdateRequest,
  AccountUpdateResponse,
  CompatibilityExtremesData,
  CurrentUser,
  MoviesInCommonData,
  HaterRanking,
  HaterRanking2,
  FilmUserComplete,
} from "../types";

const API_BASE_URL = import.meta.env.VITE_API_URL || "/api";

class ApiService {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<ApiResponse<T>> {
    const url = `${API_BASE_URL}${endpoint}`;
    const { headers: optionHeaders, ...restOptions } = options;
    const config: RequestInit = {
      ...restOptions,
      headers: {
        "Content-Type": "application/json",
        ...optionHeaders,
      },
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new ApiError(
          data.error || `Request failed with status ${response.status}`,
          response.status,
        );
      }

      return data;
    } catch (error) {
      // Intentional cancellation via AbortController isn't a failure.
      // Propagate it for the caller to handle, but don't shout in the log.
      if (error instanceof DOMException && error.name === "AbortError") {
        throw error;
      }
      console.error("API request failed:", error);
      throw error;
    }
  }

  async signup(userData: SignupRequest): Promise<ApiResponse<AuthResponse>> {
    return this.request<AuthResponse>("/auth/signup", {
      method: "POST",
      body: JSON.stringify(userData),
    });
  }

  async login(credentials: AuthRequest): Promise<ApiResponse<AuthResponse>> {
    return this.request<AuthResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify(credentials),
    });
  }

  async requestPasswordReset(email: string): Promise<ApiResponse> {
    return this.request("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  }

  async getCurrentUser(
    token: string,
    signal?: AbortSignal,
  ): Promise<ApiResponse<CurrentUser>> {
    return this.request<CurrentUser>("/auth/me", {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
      ...(signal ? { signal } : {}),
    });
  }

  async healthCheck(): Promise<ApiResponse> {
    return this.request<ApiResponse>("/health");
  }

  async getComparisonUsernames(): Promise<
    ApiResponse<Array<{ username: string; displayName?: string }>>
  > {
    return this.request<Array<{ username: string; displayName?: string }>>(
      "/comparison/usernames",
    );
  }

  async getComparisonUserRatings(
    username: string,
  ): Promise<ApiResponse<unknown>> {
    return this.request<unknown>("/comparison/user-ratings", {
      method: "POST",
      body: JSON.stringify({ username }),
    });
  }

  async compareUsers(
    user1: string,
    user2: string,
  ): Promise<ApiResponse<unknown>> {
    return this.request<unknown>("/comparison/compare", {
      method: "POST",
      body: JSON.stringify({ user1, user2 }),
    });
  }

  async getMoviesInCommon(
    user1: string,
    user2: string,
    signal?: AbortSignal,
  ): Promise<ApiResponse<MoviesInCommonData>> {
    return this.request<MoviesInCommonData>("/comparison/movies-in-common", {
      method: "POST",
      body: JSON.stringify({ user1, user2 }),
      ...(signal ? { signal } : {}),
    });
  }

  async getCompatibilityExtremes(
    username: string,
    signal?: AbortSignal,
  ): Promise<ApiResponse<CompatibilityExtremesData>> {
    return this.request<CompatibilityExtremesData>(
      `/comparison/extremes/${encodeURIComponent(username)}`,
      signal ? { signal } : {},
    );
  }

  async getHaterRankings(): Promise<ApiResponse<HaterRanking[]>> {
    return this.request<HaterRanking[]>("/comparison/hater-rankings");
  }

  async getHaterRankings2(): Promise<ApiResponse<HaterRanking2[]>> {
    return this.request<HaterRanking2[]>("/comparison/v2/hater-rankings");
  }

  async getFilmUserRatings(username: string): Promise<ApiResponse<unknown>> {
    return this.request<unknown>(`/film-users/${username}/ratings`);
  }

  async getFilmUserProfile(username: string): Promise<ApiResponse<unknown>> {
    return this.request<unknown>(`/film-users/${username}/profile`);
  }

  async getFilmUserComplete(
    username: string,
  ): Promise<ApiResponse<FilmUserComplete>> {
    return this.request<FilmUserComplete>(`/film-users/${username}/complete`);
  }

  async getFilmDetail(
    filmSlug: string,
    options: { includeNonDiscord?: boolean } = {},
    signal?: AbortSignal,
  ): Promise<ApiResponse<FilmDetail>> {
    const query = options.includeNonDiscord ? "?includeNonDiscord=true" : "";
    return this.request<FilmDetail>(
      `/films/${encodeURIComponent(filmSlug)}${query}`,
      signal ? { signal } : {},
    );
  }

  async getFilmUsers(): Promise<
    ApiResponse<Array<{ username: string; displayName?: string }>>
  > {
    return this.request<Array<{ username: string; displayName?: string }>>(
      "/film-users",
    );
  }

  // Rating distribution. Omit `year` for all-time; pass a year to scope to
  // ratings on films released that year.
  async getRatingsDistribution(
    year?: number,
    signal?: AbortSignal,
  ): Promise<ApiResponse<Array<{ rating: number; count: number }>>> {
    return this.request<Array<{ rating: number; count: number }>>(
      `/stats/total-ratings${year ? `/${year}` : ""}`,
      signal ? { signal } : {},
    );
  }

  async getAllUserFilms(): Promise<
    ApiResponse<Array<{ rating: number; count: number }>>
  > {
    return this.request<Array<{ rating: number; count: number }>>(
      "/stats/all-user-films",
    );
  }

  async getUserFilmsCount(): Promise<ApiResponse<number>> {
    return this.request<number>("/stats/user-films-count");
  }

  // Top films for a release year. Omit `year` for all-time (server returns
  // `year: null`); pass a year to scope to films released that year.
  async getTopFilmsByYear(
    year?: number,
    signal?: AbortSignal,
  ): Promise<
    ApiResponse<{
      year: number | null;
      topRated: LBFilm[];
      topWatched: LBFilm[];
    }>
  > {
    return this.request<{
      year: number | null;
      topRated: LBFilm[];
      topWatched: LBFilm[];
    }>(`/stats/top-films${year ? `/${year}` : ""}`, signal ? { signal } : {});
  }

  // Every film watched by at least 40 Discord users, most-watched first.
  async getMostWatchedFilms(
    signal?: AbortSignal,
  ): Promise<ApiResponse<LBFilm[]>> {
    return this.request<LBFilm[]>(
      "/stats/most-watched",
      signal ? { signal } : {},
    );
  }

  // Films our users rate furthest above / below the Letterboxd average. Omit
  // `year` for all-time; pass a year to scope to that release year.
  async getRatingDeviation(
    year?: number,
    signal?: AbortSignal,
  ): Promise<ApiResponse<RatingDeviationResponse>> {
    return this.request<RatingDeviationResponse>(
      `/stats/rating-deviation${year ? `/${year}` : ""}`,
      signal ? { signal } : {},
    );
  }

  // Movie Swap endpoint — bidirectional recommendations between two users.
  async getMovieSwap(
    userA: string,
    userB: string,
    signal?: AbortSignal,
  ): Promise<ApiResponse<MovieSwapResult>> {
    const params = new URLSearchParams({ userA, userB });
    return this.request<MovieSwapResult>(
      `/comparison/movie-swap?${params.toString()}`,
      signal ? { signal } : {},
    );
  }

  async getMflScoringMetrics(): Promise<ApiResponse<MFLScoringMetric[]>> {
    return this.request<MFLScoringMetric[]>("/mfl/scoring-metrics");
  }

  async getMflMovies(): Promise<ApiResponse<MFLCatalogueFilm[]>> {
    return this.request<MFLCatalogueFilm[]>("/mfl/movies");
  }

  async getMflLeaderboard(
    signal?: AbortSignal,
  ): Promise<ApiResponse<MFLLeaderboards>> {
    return this.request<MFLLeaderboards>(
      "/mfl/leaderboard",
      signal ? { signal } : {},
    );
  }

  async getMflRosters(
    token: string,
    signal?: AbortSignal,
  ): Promise<ApiResponse<MFLRoster[]>> {
    return this.request<MFLRoster[]>("/mfl/rosters", {
      headers: { Authorization: `Bearer ${token}` },
      ...(signal ? { signal } : {}),
    });
  }

  async getMflRosterPicks(
    rosterId: number,
    token: string,
    signal?: AbortSignal,
  ): Promise<ApiResponse<MFLPick[]>> {
    return this.request<MFLPick[]>(`/mfl/rosters/${rosterId}/picks`, {
      headers: { Authorization: `Bearer ${token}` },
      ...(signal ? { signal } : {}),
    });
  }

  // Public read-only view of any roster — no token.
  async getMflRosterView(
    rosterId: number,
    signal?: AbortSignal,
  ): Promise<ApiResponse<MFLRosterView>> {
    return this.request<MFLRosterView>(
      `/mfl/rosters/${rosterId}`,
      signal ? { signal } : {},
    );
  }

  async createMflRoster(
    name: string,
    filmSlugs: string[],
    isOfficial: boolean,
    token: string,
  ): Promise<ApiResponse<{ rosterId: number }>> {
    return this.request<{ rosterId: number }>("/mfl/rosters", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({ name, filmSlugs, isOfficial }),
    });
  }

  async updateMflRoster(
    rosterId: number,
    changes: { name?: string; filmSlugs?: string[]; isOfficial?: boolean },
    token: string,
  ): Promise<ApiResponse> {
    return this.request(`/mfl/rosters/${rosterId}`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(changes),
    });
  }

  async deleteMflRoster(
    rosterId: number,
    token: string,
  ): Promise<ApiResponse> {
    return this.request(`/mfl/rosters/${rosterId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  async getMflMovieScore(
    filmSlug: string,
    signal?: AbortSignal,
  ): Promise<ApiResponse<MFLMovieScore[]>> {
    return this.request<MFLMovieScore[]>(
      `/mfl/movie-score/${filmSlug}`,
      signal ? { signal } : {},
    );
  }

  // Payload object rather than positional args: token goes last by convention,
  // and scoringId is optional, so it cannot sit before a required parameter.
  async upsertMflMovieScore(
    data: {
      filmSlug: string;
      pointsAwarded: number;
      metricId: number;
      scoringId?: number;
    },
    token: string,
  ): Promise<ApiResponse<unknown>> {
    return this.request<unknown>("/mfl/admin/movie-score", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
  }

  async deleteMflMovieScore(
    scoringId: number,
    token: string,
  ): Promise<ApiResponse<unknown>> {
    return this.request<unknown>(`/mfl/admin/movie-score/${scoringId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  async getAwardShows(): Promise<ApiResponse<AwardShow[]>> {
    return this.request<AwardShow[]>("/events/award-shows");
  }

  async createAwardShow(
    data: { name: string; slug: string; description?: string },
    token: string,
  ): Promise<ApiResponse<AwardShow>> {
    return this.request<AwardShow>("/events/admin/award-shows", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
  }

  async getEvents(status?: string): Promise<ApiResponse<EventSummary[]>> {
    const query = status ? `?status=${status}` : "";
    return this.request<EventSummary[]>(`/events${query}`);
  }

  async getEventBySlug(slug: string): Promise<ApiResponse<EventData>> {
    return this.request<EventData>(`/events/${slug}`);
  }

  async submitEventPick(
    categoryId: string,
    nomineeId: string,
    token: string,
  ): Promise<ApiResponse> {
    return this.request("/events/picks", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({ categoryId, nomineeId }),
    });
  }

  async getMyEventPicks(
    slug: string,
    token: string,
  ): Promise<ApiResponse<EventUserPick[]>> {
    return this.request<EventUserPick[]>(`/events/${slug}/my-picks`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  async createEvent(
    data: {
      awardShowId: string;
      name: string;
      slug: string;
      year: number;
      editionNumber?: number;
      nominationsDate?: string;
      awardsDate?: string;
      status?: string;
    },
    token: string,
  ): Promise<ApiResponse<EventData>> {
    return this.request<EventData>("/events/admin/events", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
  }

  async updateEvent(
    id: string,
    data: Partial<{
      name: string;
      slug: string;
      year: number;
      nominationsDate: string;
      awardsDate: string;
      status: string;
    }>,
    token: string,
  ): Promise<ApiResponse> {
    return this.request(`/events/admin/events/${id}`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
  }

  async upsertEventCategory(
    data: {
      id?: string;
      eventId: string;
      name: string;
      displayOrder: number;
      displayMode?: string;
    },
    token: string,
  ): Promise<ApiResponse> {
    return this.request("/events/admin/categories", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
  }

  async deleteEventCategory(id: string, token: string): Promise<ApiResponse> {
    return this.request(`/events/admin/categories/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  async upsertEventNominee(
    data: {
      id?: string;
      categoryId: string;
      personName?: string;
      movieOrShowName: string;
      isWinner?: boolean;
    },
    token: string,
  ): Promise<ApiResponse> {
    return this.request("/events/admin/nominees", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
  }

  async deleteEventNominee(id: string, token: string): Promise<ApiResponse> {
    return this.request(`/events/admin/nominees/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  async setEventWinner(
    nomineeId: string,
    isWinner: boolean,
    token: string,
  ): Promise<ApiResponse> {
    return this.request(`/events/admin/nominees/${nomineeId}/winner`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({ isWinner }),
    });
  }

  async pathFinder(
    actor1: string,
    actor2: string,
    signal?: AbortSignal,
  ): Promise<ApiResponse> {
    // Endpoint is public; no auth header.
    return this.request(`/actor-graph/path-finder/${actor1}/${actor2}`, {
      method: "GET",
      ...(signal ? { signal } : {}),
    });
  }

  // Combined actor + movie search backed by /api/actor-graph/search.
  // Returns DB-cached hits first, then TMDB results for items not yet seeded.
  async searchGraph(query: string, signal?: AbortSignal): Promise<ApiResponse> {
    return this.request(`/actor-graph/search?q=${encodeURIComponent(query)}`, {
      method: "GET",
      ...(signal ? { signal } : {}),
    });
  }

  async triggerRefresh(
    token: string,
  ): Promise<ApiResponse<{ job_id: string }>> {
    return this.request<{ job_id: string }>("/admin/refresh-rankings", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  async getRefreshJob(
    id: string,
    token: string,
  ): Promise<ApiResponse<RefreshJob>> {
    return this.request<RefreshJob>(`/admin/refresh-rankings/${id}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  async cancelRefreshJob(
    id: string,
    token: string,
  ): Promise<ApiResponse<{ id: string; status: "cancelled" }>> {
    return this.request<{ id: string; status: "cancelled" }>(
      `/admin/refresh-rankings/${id}/cancel`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      },
    );
  }

  async triggerScrapeUser(
    username: string,
    token: string,
  ): Promise<ApiResponse<{ job_id: string }>> {
    return this.request<{ job_id: string }>(
      `/scrape-user/${encodeURIComponent(username)}`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      },
    );
  }

  async getScrapeJob(
    id: string,
    token: string,
  ): Promise<ApiResponse<UserScrapeJob>> {
    return this.request<UserScrapeJob>(`/scrape-user/jobs/${id}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  async cancelScrapeJob(
    id: string,
    token: string,
  ): Promise<ApiResponse<{ id: string; status: "cancelled" }>> {
    return this.request<{ id: string; status: "cancelled" }>(
      `/scrape-user/jobs/${id}/cancel`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      },
    );
  }

  async refreshUser(
    username: string,
    token: string,
  ): Promise<
    ApiResponse<{ lbusername: string; watch_items: number; upserted: number }>
  > {
    return this.request<{
      lbusername: string;
      watch_items: number;
      upserted: number;
    }>(`/scrape-user/${encodeURIComponent(username)}/refresh`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  async getAccounts(token: string): Promise<ApiResponse<AccountView[]>> {
    return this.request<AccountView[]>("/admin/users", {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  async updateAccount(
    id: string,
    patch: AccountUpdateRequest,
    token: string,
  ): Promise<ApiResponse<AccountUpdateResponse>> {
    return this.request<AccountUpdateResponse>(`/admin/users/${id}`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(patch),
    });
  }

  async deleteAccount(
    id: string,
    token: string,
  ): Promise<ApiResponse<{ id: string; deleted: true }>> {
    return this.request<{ id: string; deleted: true }>(`/admin/users/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
  }
}

export const apiService = new ApiService();
export default apiService;
